import React, { useState, useEffect } from 'react';
import { useAuth } from '../../../../context/AuthContext';
import api from '../../../../services/api';
import { getDistanceLabel } from '../../utils/distanceUtils';
import { useBodyScrollLock } from '../../hooks/useBodyScrollLock';
import './TaskModals.css';

const ActionModal = ({ 
  isOpen,
  onClose,
  selectedTask,
  actionType,
  departments = [],
  employees = [],
  employeeLocations = [],
  onAction,
  isLoadingEmployees = false
}) => {
  const { company, user } = useAuth();
  const [actionNote, setActionNote] = useState('');
  const [selectedDepartment, setSelectedDepartment] = useState(null);
  const [selectedSupervisor, setSelectedSupervisor] = useState('');
  const [selectedEmployee, setSelectedEmployee] = useState('');
  const [filteredEmployees, setFilteredEmployees] = useState([]);
  const [supervisors, setSupervisors] = useState([]);
  const [loading, setLoading] = useState(false);
  const filteredSupervisors = selectedDepartment
    ? supervisors.filter((sup) => {
        if (sup.role === 'admin') {
          return true;
        }
        return sup.departments && sup.departments.some((dept) => dept.id === parseInt(selectedDepartment, 10));
      })
    : [];

  // Extract user's department IDs (use supervisors list if available)
  const getUserDepartmentIds = () => {
    if (!user) return [];

    if (Array.isArray(supervisors) && supervisors.length > 0) {
      const me = supervisors.find((sup) => sup.id === user.id);
      if (me && Array.isArray(me.departments) && me.departments.length > 0) {
        return me.departments.map((d) => (typeof d === 'object' ? d.id ?? d : d));
      }
    }

    if (Array.isArray(user.departments) && user.departments.length > 0) {
      return user.departments.map((d) => (typeof d === 'object' ? d.id ?? d : d));
    }
    if (user.departmentId) return [user.departmentId];
    if (user.department_id) return [user.department_id];
    if (user.department) {
      if (typeof user.department === 'object' && user.department.id) return [user.department.id];
      return [user.department];
    }
    return [];
  };

  // Permission: allow if admin or belongs to same department (or task has no department yet)
  const userDeptIds = getUserDepartmentIds();
  const hasDepartmentPermission = user?.role === 'admin' || !selectedTask?.department?.id || userDeptIds.includes(selectedTask?.department?.id);

  useEffect(() => {
    if (isOpen && selectedTask) {
      // Reset form when modal opens
      setActionNote('');
      
      // Pre-populate department if task already has one
      if (selectedTask.department?.id) {
        setSelectedDepartment(selectedTask.department.id);
        filterEmployeesByDepartment(selectedTask.department.id);
        
        // Pre-populate supervisor if task already has one assigned
        if (selectedTask.supervisor?.id) {
          setSelectedSupervisor(selectedTask.supervisor.id.toString());
        } else if (selectedTask.supervisorAssigned) {
          setSelectedSupervisor(selectedTask.supervisorAssigned.toString());
        } else {
          setSelectedSupervisor('');
        }
        
        // Pre-populate employee if task already has one assigned
        if (selectedTask.assignedEmployee?.id) {
          setSelectedEmployee(selectedTask.assignedEmployee.id.toString());
        } else if (selectedTask.employeeAssigned) {
          setSelectedEmployee(selectedTask.employeeAssigned.toString());
        } else {
          setSelectedEmployee('');
        }
        
        // Pre-populate assignment notes with existing supervisor notes for reassignment
        setActionNote(selectedTask.supervisorNotes || '');
      } else {
        setSelectedDepartment(null);
        setFilteredEmployees([]);
        setSelectedSupervisor('');
        setSelectedEmployee('');
        // Reset notes when no department (new assignment)
        setActionNote('');
      }
    }
  }, [isOpen, selectedTask]);

  // Fetch supervisors when component mounts or company changes
  useEffect(() => {
    if (company?.id && isOpen) {
      fetchSupervisors();
    }
  }, [company?.id, isOpen]);

  const fetchSupervisors = async () => {
    try {
      setLoading(true);
      const params = user?.role === 'supervisor' ? { supervisorId: user.id } : {};
      const response = await api.get('/api/companies/users', { params });
      
      if (response.data?.is_success) {
        console.log('Fetched users:', response.data.users);
        const supervisorList = response.data.users.filter(user => 
            (user.role === 'admin' || user.role === 'supervisor') && user.isActive
        );
        console.log('Fetched supervisors:', supervisorList);
        setSupervisors(supervisorList);
      }
    } catch (error) {
      console.error('Error fetching supervisors:', error);
      setSupervisors([]);
    } finally {
      setLoading(false);
    }
  };

  const filterEmployeesByDepartment = (departmentId) => {
    if (!departmentId) {
      setFilteredEmployees([]);
      return;
    }
    
    // Filter employees who belong to the selected department
    const filtered = employees.filter(emp =>
      emp.departments && emp.departments.some(dept => dept.id == departmentId)
    );
    setFilteredEmployees(filtered);
  };

  const handleDepartmentChange = (departmentId) => {
    setSelectedDepartment(departmentId);
    setSelectedEmployee(''); // Reset employee selection
    setSelectedSupervisor(''); // Reset supervisor selection when department changes
    filterEmployeesByDepartment(departmentId);
  };

  const handleSubmit = () => {
    const actionData = {};

    switch (actionType) {
      case 'reject':
        if (!actionNote) {
          alert('Please provide a reason for rejection');
          return;
        }
        actionData.status = 'rejected';
        actionData.supervisorNotes = actionNote;
        break;

      case 'assign-dept':
        if (!selectedDepartment) {
          alert('Please select a department');
          return;
        }
        actionData.departmentId = selectedDepartment;
        if (selectedSupervisor) {
          actionData.supervisorAssigned = parseInt(selectedSupervisor);
        }
        break;

      case 'assign-task':
        if (!selectedEmployee) {
          alert('Please select an employee');
          return;
        }
        actionData.employeeAssigned = selectedEmployee;
        actionData.supervisorNotes = 'Assigned to employee';
        actionData.status = 'pending';
        break;

      case 'assign-both':
        if (!selectedDepartment) {
          alert('Please select a department');
          return;
        }
        actionData.departmentId = selectedDepartment;
        
        // Include supervisor if selected
        if (selectedSupervisor && selectedSupervisor.trim() !== '') {
          actionData.supervisorAssigned = parseInt(selectedSupervisor);
        }
        
        // Include employee if selected, otherwise task remains incoming status
        if (selectedEmployee && selectedEmployee.trim() !== '') {
          actionData.employeeAssigned = parseInt(selectedEmployee);
        }
        // When any assignment (supervisor or employee) happens, set to pending; otherwise incoming
        actionData.status = (actionData.supervisorAssigned || actionData.employeeAssigned) ? 'pending' : 'incoming';
        
        actionData.supervisorNotes = actionNote.trim() || 'Task assigned via quick assignment';
        break;

      case 'resolve':
        // Allow optional resolution notes
        actionData.status = 'resolved';
        if (actionNote && actionNote.trim() !== '') {
          actionData.supervisorNotes = actionNote.trim();
        }
        break;

      default:
        return;
    }

    onAction(selectedTask.id, actionData);
  };

  // Lock body when open
  useBodyScrollLock(isOpen && !!selectedTask);

  // Close on ESC
  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.key === 'Escape') {
        onClose && onClose();
      }
    };
    if (isOpen && selectedTask) {
      window.addEventListener('keydown', onKeyDown);
    }
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [isOpen, selectedTask, onClose]);

  if (!isOpen || !selectedTask) return null;

  // If user lacks permission, show simple notice and block actions
  if (!hasDepartmentPermission) {
    return (
      <div className="modal-overlay task-modal-overlay" onClick={onClose}>
        <div className="modal-content task-modal-container" onClick={(e) => e.stopPropagation()}>
          <div className="modal-header task-modal-header">
            <div className="task-modal-header-content">
              <div className="task-title-row">
                <span className="task-id-pill">Permission Denied</span>
              </div>
            </div>
            <button className="close-button" onClick={onClose}>×</button>
          </div>
          <div className="modal-body task-modal-body">
            <p>You do not have permission to perform this action.</p>
          </div>
          <div className="modal-footer task-modal-footer">
            <button className="btn btn-secondary" onClick={onClose}>Close</button>
          </div>
        </div>
      </div>
    );
  }

  const getModalTitle = () => {
    // Check if this is a reassignment (task already has assignments)
    const isReassignment = selectedTask?.department?.id || selectedTask?.supervisor?.id || selectedTask?.assignedEmployee?.id || selectedTask?.supervisorAssigned || selectedTask?.employeeAssigned;
    
    switch (actionType) {
      case 'reject': return 'Reject Task';
      case 'resolve': return 'Resolve Task';
      case 'assign-dept': return isReassignment ? 'Reassign Department' : 'Assign Department';
      case 'assign-task': return isReassignment ? 'Reassign Employee' : 'Assign Employee';
      case 'assign-both': return isReassignment ? 'Reassign Department & Employee' : 'Assign Department & Employee';
      default: return 'Task Action';
    }
  };

  const getSubmitButtonText = () => {
    // Check if this is a reassignment (task already has assignments)
    const isReassignment = selectedTask?.department?.id || selectedTask?.supervisor?.id || selectedTask?.assignedEmployee?.id || selectedTask?.supervisorAssigned || selectedTask?.employeeAssigned;
    
    switch (actionType) {
      case 'reject': return 'Reject Task';
      case 'resolve': return 'Resolve';
      default: return isReassignment ? 'Reassign' : 'Assign';
    }
  };

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) onClose && onClose();
  };

  return (
    <div className="modal-overlay task-modal-overlay action-modal-overlay" onClick={handleOverlayClick}>
      <div className="modal-content task-modal-container" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header task-modal-header">
          <div className="task-modal-header-content">
            <div className="task-title-row">
              <span className="task-id-pill">{getModalTitle()}</span>
            </div>
          </div>
          <button className="close-button" onClick={onClose} aria-label="Close">
            ×
          </button>
        </div>

        <div className="modal-body task-modal-body">
          {(actionType === 'reject' || actionType === 'resolve') && (
            <div className="form-group">
              <label>{actionType === 'reject' ? 'Reason for Rejection' : 'Resolution Notes (optional)'}</label>
              <textarea
                className="form-control"
                value={actionNote}
                onChange={(e) => setActionNote(e.target.value)}
                rows="3"
                placeholder={actionType === 'reject' ? 'Enter reason for rejection' : 'Add any resolution notes'}
              />
            </div>
          )}

          {(actionType === 'assign-dept' || actionType === 'assign-task' || actionType === 'assign-both') && (
            <div className="form-group">
              <label>Assignment Details</label>
              <div className="combined-inputs">
                {(actionType === 'assign-dept' || actionType === 'assign-both') && (
                  <select
                    className="form-control"
                    value={selectedDepartment || ''}
                    onChange={(e) => handleDepartmentChange(e.target.value)}
                  >
                    <option value="">Select Team</option>
                    {departments.map(dept => (
                      <option key={dept.id} value={dept.id}>
                        {dept.name}
                      </option>
                    ))}
                  </select>
                )}

                {(actionType === 'assign-dept' || actionType === 'assign-both') && selectedDepartment && (
                  <select
                    className="form-control"
                    value={selectedSupervisor}
                    onChange={(e) => setSelectedSupervisor(e.target.value)}
                    disabled={loading}
                  >
                    <option value="">
                      {loading ? "Loading supervisors..." : "Supervisor (optional)"}
                    </option>
                    {supervisors
                      .filter(sup => {
                        if (sup.role === 'admin') {
                          return true;
                        }
                        return sup.departments && sup.departments.some(dept => 
                          dept.id === parseInt(selectedDepartment)
                        );
                      })
                      .map(supervisor => (
                        <option key={supervisor.id} value={supervisor.id}>
                          {supervisor.firstName} {supervisor.lastName}
                        </option>
                      ))
                    }
                  </select>
                )}

                {(actionType === 'assign-task' || actionType === 'assign-both') && (
                  <select
                    className="form-control"
                    value={selectedEmployee}
                    onChange={(e) => setSelectedEmployee(e.target.value)}
                    disabled={actionType === 'assign-both' && !selectedDepartment}
                  >
                    <option value="">
                      {actionType === 'assign-both' && !selectedDepartment
                        ? "Select a team first" 
                        : isLoadingEmployees
                          ? "Loading employees..."
                          : filteredEmployees.length === 0 
                            ? "No employees in this team" 
                            : "Field Staff (optional)"}
                    </option>
                    {filteredEmployees.map(emp => (
                      <option key={emp.id} value={emp.id}>
                        {emp.firstName} {emp.lastName} {getDistanceLabel(emp, selectedTask?.location, employeeLocations)}
                      </option>
                    ))}
                  </select>
                )}

                {actionType === 'assign-both' && (
                  <textarea
                    className="form-control"
                    value={actionNote}
                    onChange={(e) => setActionNote(e.target.value)}
                    rows="3"
                    placeholder="Assignment notes"
                  />
                )}
              </div>
              {(actionType === 'assign-dept' || actionType === 'assign-both') && selectedDepartment && !loading && filteredSupervisors.length === 0 && (
                <small className="form-text text-muted">No supervisors found for this team</small>
              )}
              {(actionType === 'assign-task' || actionType === 'assign-both') && selectedDepartment && !isLoadingEmployees && filteredEmployees.length === 0 && (
                <small className="form-text text-muted">No employees in this team</small>
              )}
            </div>
          )}
        </div>

        <div className="modal-footer task-modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
          <button className="submit-button" onClick={handleSubmit}>{getSubmitButtonText()}</button>
        </div>
      </div>
    </div>
  );
};

export default ActionModal; 